import React, { useEffect, useState, useContext } from 'react';
import classNames from 'classnames';
import { RootContext } from '../../hoc/RootContext/RootContext';
import styles from './RecentProfiles.module.css';
import RecentProfileCard from '../../components/RecentProfileCard/RecentProfileCard';
import axios, { CancelToken, isCancel } from '../../utils/axios';
import { PageTitle, TagSelector, States } from '@project/components';
import AdminLogout from '../AdminLogout/AdminLogout';
import MenuToggle from './../Shared/MenuToggle/MenuToggle';
import { STATUS_LABELS } from '../../constants';
import { Spinner } from 'react-bootstrap';
import { useDebounce } from '../../utils/hooks';
import { useIntl } from 'react-intl';
import ChevronLeft from '../../assets/images/chevron-left';
import ChevronRight from '../../assets/images/chevron-right';
import { get } from '../../utils/utils';

function RecentProfiles() {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  const { currentUser: { displayCompanies } } = useContext(RootContext);

  const [profileData, setProfileData] = useState({
    profiles: [],
    pagination: { count: null, page: null, limit: null }
  });
  const [profileFilter, setProfileFilter] = useState('');
  const [filterTags, setFilterTags] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterTagOptions, setFilterTagOptions] = useState([]);
  const [properties, setProperties] = useState([]);
  const [page, setPage] = useState(1);

  const debouncedSearchFilter = useDebounce(profileFilter, 400);
  const debouncedTagFilter = useDebounce(filterTags, 400);

  const ITEMS_PER_PAGE = 30;

  useEffect(() => {
    setIsLoading(true);
    const source = CancelToken.source();

    async function fetchProfiles() {
      let tagQuery = filterTags.map(tag => `${tag.split(': ')[0].toLowerCase()}=${tag.split(': ')[1]}`);
      let tagQueryString = tagQuery.join('&');
      let normalizedPage = page
      try {
        const result = await axios.get(`/space-profiles?parentOnly=true&page=${normalizedPage}&limit=${ITEMS_PER_PAGE}&search=${encodeURIComponent(debouncedSearchFilter)}${tagQueryString ? `&${tagQueryString}` : ''}`, {
          cancelToken: source.token
        });
        const profiles = result.data.data;

        setProfileData({
          profiles,
          pagination: {
            count: get(result, 'headers.pagination-count', null)
          }
        });
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
        if (isCancel(e)) {
          // request is cancelled
        } else {
          throw e;
        }
      }
    }
    fetchProfiles();
    return () => {
      setIsLoading(false);
      source.cancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchFilter, debouncedTagFilter, page]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchFilter, debouncedTagFilter])

  useEffect(() => {
    const publishStatusOptions = [
      { id: 'published', label: `${t('SearchTable.status')}: ${t('SearchTable.published')}`, value: `Status: published` },
      { id: 'unpublished', label: `${t('SearchTable.status')}: ${t('SearchTable.unpublished')}`, value: `Status: unpublished` },
      { id: 'draft', label: `${t('SearchTable.status')}: ${t('SearchTable.draft')}`, value: `Status: draft` }
    ];

    const stateOptions = States.map(state => {
      return { id: state.value, label: `${t('term.state')}: ${state.value}`, value: `State: ${state.label.split(' ')[0]}` }
    });

    setFilterTagOptions(prevFilterTagOptions => [...publishStatusOptions, ...stateOptions, ...prevFilterTagOptions].sort());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const retrieveAllProperties = async () => {
      try {
        const result = await axios.get(`/properties-datalake`, {
          params: {
            selectFields: ['name', 'company']
          }
        });

        if (result.status === 200) {
          const tmpProperties = result.data.records.map(property => {
            if (!property.displayName) property.displayName = property.name
            return property
          })
          let properties = tmpProperties.map(_ => { return { id: _.name, label: `${t('term.property')}: ${_.displayName}`, value: `Property: ${_.name}` }; });

          setProperties(result.data.records || []);
          setFilterTagOptions(prevFilterTagOptions => [...prevFilterTagOptions, ...properties].sort());
        }
      } catch (e) {
        console.error(e);
      }
    }

    retrieveAllProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let companyOptions = (displayCompanies || []).map(company => {
      return { id: company, label: `${t('term.company')}: ${company.displayName}`, value: `Company: ${company.name}` };
    });

    setFilterTagOptions(prevFilterTagOptions => [...companyOptions, ...prevFilterTagOptions].sort());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayCompanies])

  useEffect(() => {
    let propertyTag = filterTags.find(tag => tag.includes('Property:'));
    let companyTag = filterTags.find(tag => tag.includes('Company:'));

    if (propertyTag && !companyTag) {
      let property = properties.find(_ => _.name === propertyTag.split(': ')[1])

      if (property) setFilterTags(prevValues => {
        let newTags = prevValues.filter(_ => !_.includes('Company:'));
        return [`Company: ${property.company}`, ...newTags];
      })
    }
  }, [filterTags, setFilterTags, properties]);

  const getCardText = (status, modified) => {
    const date = intl.formatDate(new Date(modified));
    const time = intl.formatTime(new Date(modified));

    return <><span className={classNames(styles.status, 'text-capitalize')}>{STATUS_LABELS[status] || status}</span> {date} {time}</>;
  }

  const selectTag = (values) => {
    setFilterTags(values);
  }

  const clearTagsHandler = () => {
    setFilterTags([]);
  }

  let companyTag = filterTags.find(tag => tag.includes('Company:'));

  const filterCategoriesSelected = filterTags.map(_ => `${_.split(': ')[0]}:`);

  let filterTagOptionsModified = [...filterTagOptions];

  if (companyTag) {
    let companyPropertyOptions = properties.filter(_ => _.company === companyTag.split(': ')[1]).map(_ => { return { id: _.name, label: `${t('term.property')}: ${_.displayName}`, value: `Property: ${_.name}` }; })
    filterTagOptionsModified = filterTagOptionsModified.filter(_ => !_.value.includes('Property:'));
    filterTagOptionsModified = [...filterTagOptionsModified, ...companyPropertyOptions];
  }

  return (
    <section className={classNames(styles.recentProfilesPage)}>
      <PageTitle title={t('RecentProfiles.pageTitle')} />
      <header>
        <div className='d-flex'>
          <MenuToggle />
          <h3 className='is-page-title ml-3'>{t('RecentProfiles.title')}</h3>
        </div>
        <AdminLogout />
      </header>
      <main>
        <div className="d-flex flex-wrap ml-4 pl-2 mb-4">
          <div className="w-100">
            <TagSelector
              options={filterTagOptionsModified}
              values={filterTags || []}
              setValues={(values) => selectTag(values)}
              label={t('RecentProfiles.searchFilter')}
              className={styles.tagSelector}
              customCategoryFilter={option => !filterCategoriesSelected.some(category => option.value.includes(category))}
              setCustomSearch={setProfileFilter}
              clearHandler={filterTags.length >= 2 ? clearTagsHandler : null}
              hideSelectedTags={!filterTags.length}
              identifierClass={'data-tag-selector'}
            />
          </div>

        </div>
        <div className="w-100 my-2 d-flex justify-content-center">
          <div className="ml-2">{isLoading && <Spinner animation="grow" variant="primary" />}</div>
        </div>
        <div className="d-flex flex-wrap">
          {!isLoading && profileData.profiles.map(({ guid, property_display_name, address1, address2, city, featuredThumbnailUpload, featuredUpload, status, modified }) =>
            <RecentProfileCard
              key={guid}
              guid={guid}
              cardImg={featuredThumbnailUpload || featuredUpload}
              cardTitle={property_display_name}
              cardSubtitle={intl.formatMessage({ id: 'RecentProfiles.address' }, { address1, address2, city })}
              cardText={getCardText(status, modified)}
            />)
          }
          {!profileData.profiles.length && profileFilter ? <div className="ml-4 pl-2">{t('RecentProfiles.searchNoMatches')}</div> : null}
        </div>
        {profileData.profiles.length && !isLoading && (!!profileData.pagination.count && profileData.pagination.count > ITEMS_PER_PAGE) ?
          <>
            <div
              data-testid="paginator"
              className={`d-flex justify-content-center align-items-center w-100 ${styles.paginator}`}
            >
              <div className="d-flex">
                <div
                  className={classNames(
                    styles.leftChevron,
                    {
                      [styles.paginateDisabled]: page === 1
                    },
                    'd-flex justify-content-center align-items-center'
                  )}
                  onClick={() => setPage(prevPage => prevPage === 1 ? prevPage : prevPage - 1)}
                >
                  <div data-testid='left-click'>
                    <ChevronLeft />
                  </div>
                </div>
                <div
                  className={classNames(
                    styles.rightChevron,
                    {
                      [styles.paginateDisabled]: page * ITEMS_PER_PAGE >= profileData.pagination.count
                    },
                    'd-flex justify-content-center align-items-center'
                  )}
                  onClick={() => setPage(prevPage => prevPage * ITEMS_PER_PAGE >= profileData.pagination.count ? prevPage : prevPage + 1)}
                >
                  <div data-testid='right-click'>
                    <ChevronRight />
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.BottomSpacerForIOS}>&nbsp;</div>
          </>
          : null}
      </main>
    </section>
  )
}

export default RecentProfiles;